import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, ClockIcon } from 'lucide-react';
import { CalendarProps } from './types';
import { CalendarDay } from './CalendarDay';
import { generateCalendarGrid, getMonthName, getYear, getPreviousMonth, getNextMonth, isSameDay, isToday, isDateDisabled } from './utils';
const WEEKDAY_LABELS = ['SU', 'MO', 'TU', 'WE', 'TH', 'FR', 'SA'];
export const Calendar: React.FC<CalendarProps> = ({
  value,
  onChange,
  minDate,
  maxDate,
  disabledDates,
  includeTime = false,
  minTime,
  maxTime,
  className = '',
  'data-id': dataId
}) => {
  const [currentMonth, setCurrentMonth] = useState(value || new Date());
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const calendarRef = useRef<HTMLDivElement>(null);
  // Initialize time from value
  useEffect(() => {
    if (value && includeTime) {
      setHours(value.getHours().toString().padStart(2, '0'));
      setMinutes(value.getMinutes().toString().padStart(2, '0'));
    }
  }, [value, includeTime]);
  const grid = generateCalendarGrid(currentMonth);
  const handlePreviousMonth = () => {
    setCurrentMonth(getPreviousMonth(currentMonth));
  };
  const handleNextMonth = () => {
    setCurrentMonth(getNextMonth(currentMonth));
  };
  const combineDateTime = (date: Date, timeHours: string, timeMinutes: string): Date => {
    const combined = new Date(date);
    if (timeHours && timeMinutes) {
      combined.setHours(parseInt(timeHours, 10), parseInt(timeMinutes, 10), 0, 0);
    }
    return combined;
  };
  const handleDateClick = (date: Date) => {
    if (onChange) {
      if (includeTime && hours && minutes) {
        onChange(combineDateTime(date, hours, minutes));
      } else {
        onChange(date);
      }
    }
  };
  const handleTimeChange = (newHours: string, newMinutes: string) => {
    setHours(newHours);
    setMinutes(newMinutes);
    if (value && newHours.length === 2 && newMinutes.length === 2 && onChange) {
      onChange(combineDateTime(value, newHours, newMinutes));
    }
  };
  const isTimeValid = (h: string, m: string): boolean => {
    const hourNum = parseInt(h, 10);
    const minuteNum = parseInt(m, 10);
    if (isNaN(hourNum) || isNaN(minuteNum)) return false;
    if (hourNum < 0 || hourNum > 23) return false;
    if (minuteNum < 0 || minuteNum > 59) return false;
    const timeString = `${h.padStart(2, '0')}:${m.padStart(2, '0')}`;
    if (minTime && timeString < minTime) return false;
    if (maxTime && timeString > maxTime) return false;
    return true;
  };
  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length > 2) val = val.slice(0, 2);
    const numVal = parseInt(val, 10);
    if (!isNaN(numVal) && numVal > 23) val = '23';
    setHours(val);
    if (val.length === 2 && minutes.length === 2) {
      handleTimeChange(val, minutes);
    }
  };
  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value.replace(/\D/g, '');
    if (val.length > 2) val = val.slice(0, 2);
    const numVal = parseInt(val, 10);
    if (!isNaN(numVal) && numVal > 59) val = '59';
    setMinutes(val);
    if (hours.length === 2 && val.length === 2) {
      handleTimeChange(hours, val);
    }
  };
  const handleHoursBlur = () => {
    if (hours.length === 1) {
      const padded = hours.padStart(2, '0');
      setHours(padded);
      if (minutes.length === 2) {
        handleTimeChange(padded, minutes);
      }
    }
  };
  const handleMinutesBlur = () => {
    if (minutes.length === 1) {
      const padded = minutes.padStart(2, '0');
      setMinutes(padded);
      if (hours.length === 2) {
        handleTimeChange(hours, padded);
      }
    }
  };
  const handleHoursKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const current = parseInt(hours || '0', 10);
      const next = current >= 23 ? 0 : current + 1;
      const nextStr = next.toString().padStart(2, '0');
      setHours(nextStr);
      if (minutes.length === 2) {
        handleTimeChange(nextStr, minutes);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const current = parseInt(hours || '0', 10);
      const prev = current <= 0 ? 23 : current - 1;
      const prevStr = prev.toString().padStart(2, '0');
      setHours(prevStr);
      if (minutes.length === 2) {
        handleTimeChange(prevStr, minutes);
      }
    }
  };
  const handleMinutesKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      const current = parseInt(minutes || '0', 10);
      const next = current >= 59 ? 0 : current + 1;
      const nextStr = next.toString().padStart(2, '0');
      setMinutes(nextStr);
      if (hours.length === 2) {
        handleTimeChange(hours, nextStr);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const current = parseInt(minutes || '0', 10);
      const prev = current <= 0 ? 59 : current - 1;
      const prevStr = prev.toString().padStart(2, '0');
      setMinutes(prevStr);
      if (hours.length === 2) {
        handleTimeChange(hours, prevStr);
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent, date: Date) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleDateClick(date);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      if (onChange) {
        onChange(undefined as any);
      }
    } else if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      navigateWithArrows(e.key, date);
    }
  };
  const navigateWithArrows = (key: string, currentDate: Date) => {
    let newDate = new Date(currentDate);
    switch (key) {
      case 'ArrowLeft':
        newDate.setDate(newDate.getDate() - 1);
        break;
      case 'ArrowRight':
        newDate.setDate(newDate.getDate() + 1);
        break;
      case 'ArrowUp':
        newDate.setDate(newDate.getDate() - 7);
        break;
      case 'ArrowDown':
        newDate.setDate(newDate.getDate() + 7);
        break;
    }
    if (newDate.getMonth() !== currentMonth.getMonth()) {
      setCurrentMonth(newDate);
    }
    setTimeout(() => {
      const buttons = calendarRef.current?.querySelectorAll('button[role="gridcell"]');
      buttons?.forEach(button => {
        const buttonDate = new Date(button.getAttribute('aria-label')?.split('Select ')[1] || '');
        if (!isNaN(buttonDate.getTime()) && isSameDay(buttonDate, newDate)) {
          ;
          (button as HTMLButtonElement).focus();
        }
      });
    }, 0);
  };
  useEffect(() => {
    if (value) {
      setCurrentMonth(value);
    }
  }, [value]);
  return <div ref={calendarRef} className={`bg-white rounded-lg shadow-sm p-6 w-fit ${className}`} role="application" aria-label="Calendar" data-id="element-440">
      {/* Header with month/year and navigation */}
      <div className="flex items-center justify-between mb-6" data-id="element-441">
        <button type="button" onClick={handlePreviousMonth} className="p-2 rounded-md hover:bg-[#F8FAFC] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2" aria-label="Previous month" data-id="element-442">
          <ChevronLeftIcon className="w-5 h-5 text-[#475569]" data-id="element-443" />
        </button>

        <h2 className="text-[14px] font-semibold text-[#0F172A]" data-id="element-444">
          {getMonthName(currentMonth)} {getYear(currentMonth)}
        </h2>

        <button type="button" onClick={handleNextMonth} className="p-2 rounded-md hover:bg-[#F8FAFC] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#0EA5E9] focus:ring-offset-2" aria-label="Next month" data-id="element-445">
          <ChevronRightIcon className="w-5 h-5 text-[#475569]" data-id="element-446" />
        </button>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 gap-1 mb-2" role="row" data-id="element-447">
        {WEEKDAY_LABELS.map(day => <div key={day} className="flex items-center justify-center w-12 h-8 text-[12px] font-medium text-[#94A3B8]" role="columnheader" data-id="element-448">
            {day}
          </div>)}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7 gap-1" role="grid" data-id="element-449">
        {grid.map((date, index) => {
        if (!date) {
          return <div key={`empty-${index}`} className="w-12 h-12" role="gridcell" aria-hidden="true" data-id="element-450" />;
        }
        const selected = value ? isSameDay(date, value) : false;
        const today = isToday(date);
        const currentMonthDate = date.getMonth() === currentMonth.getMonth();
        const disabled = isDateDisabled(date, minDate, maxDate, disabledDates);
        return <CalendarDay key={date.toISOString()} date={date} isSelected={selected} isToday={today} isCurrentMonth={currentMonthDate} isDisabled={disabled} onClick={handleDateClick} onKeyDown={handleKeyDown} data-id="element-451" />;
      })}
      </div>

      {/* Time Picker Section */}
      {includeTime && <div className="mt-6 pt-6 border-t border-[#E2E8F0]" data-id="element-452">
          <div className="flex items-center gap-2 mb-4" data-id="element-453">
            <ClockIcon className="w-4 h-4 text-[#475569]" data-id="element-454" />
            <h3 className="text-[13px] font-medium text-[#475569]" data-id="element-455">Time</h3>
          </div>

          <div className="flex items-center gap-2" data-id="element-456">
            {/* Hours Input */}
            <div className="flex flex-col" data-id="element-457">
              <label htmlFor="hours-input" className="text-[12px] font-medium text-[#94A3B8] mb-1" data-id="element-458">
                Hours
              </label>
              <input id="hours-input" type="text" inputMode="numeric" value={hours} onChange={handleHoursChange} onBlur={handleHoursBlur} onKeyDown={handleHoursKeyDown} placeholder="00" maxLength={2} className="w-16 h-10 px-3 text-center text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all duration-200" aria-label="Hours (0-23)" data-id="element-459" />
            </div>

            {/* Separator */}
            <div className="text-[16px] font-semibold text-[#475569] mt-5" data-id="element-460">
              :
            </div>

            {/* Minutes Input */}
            <div className="flex flex-col" data-id="element-461">
              <label htmlFor="minutes-input" className="text-[12px] font-medium text-[#94A3B8] mb-1" data-id="element-462">
                Minutes
              </label>
              <input id="minutes-input" type="text" inputMode="numeric" value={minutes} onChange={handleMinutesChange} onBlur={handleMinutesBlur} onKeyDown={handleMinutesKeyDown} placeholder="00" maxLength={2} className="w-16 h-10 px-3 text-center text-[14px] font-medium text-[#0F172A] bg-white border border-[#CBD5E1] rounded-md focus:outline-none focus:border-[#0EA5E9] focus:ring-2 focus:ring-[#0EA5E9] focus:ring-opacity-20 transition-all duration-200" aria-label="Minutes (0-59)" data-id="element-463" />
            </div>
          </div>

          <p className="text-[12px] text-[#94A3B8] mt-2" data-id="element-464">24-hour format</p>
        </div>}
    </div>;
};